import React, { useState } from 'react';
import Button from '../common/Button';
import { LEARN_COPY } from '../../config/learnCopy';

/**
 * One age question, asked AFTER the first challenge and before the account (KAN-491).
 *
 * WHY IT EXISTS. `create_flare_session` refuses to store anything not confirmed 13+, so a
 * learner's first challenge could not be written until an account existed — which is why it
 * was held in the browser instead. That hold cannot cross a browser: a learner who opens the
 * confirmation mail on their phone signs in somewhere that has never seen it, and the work is
 * gone. Asking the age here lets the session be stored immediately, so only its ID has to
 * travel.
 *
 * WHY HERE and not earlier. Mr. Chen, 17 September, item 4: "Do not place registration and all
 * questionnaires before the first experience." This is after the challenge, at the moment we
 * offer to keep it — which is the first point the question is about something the learner
 * wants rather than a gate they must pass.
 *
 * THE COPY IS NOT NEW. It is `LEARN_COPY.onboarding.age`, already written and already used by
 * the onboarding wizard. Reusing it rather than inventing a second way of asking the same
 * question keeps one voice, and means no learner-facing string here has bypassed review.
 *
 * WHAT THIS COMPONENT DOES NOT DECIDE. It reports the year. Whether an under-13 answer refuses
 * the account, routes to a guardian, or does something else is a product and legal decision
 * that has not been made — see KAN-491. This calls `onAnswer(birthYear, isEligible)` and lets
 * the caller act; it deliberately does not block anyone by itself, because a block nobody
 * ruled on is a policy invented in a component.
 */
const CURRENT_YEAR = new Date().getFullYear();

export function ageFromBirthYear(birthYear) {
  const y = parseInt(birthYear, 10);
  if (!Number.isFinite(y) || y < 1900 || y > CURRENT_YEAR) return null;
  return CURRENT_YEAR - y;
}

/** 13+ is the threshold the server enforces; this mirrors it rather than inventing one. */
export function isEligibleToStore(birthYear) {
  const age = ageFromBirthYear(birthYear);
  return age !== null && age >= 13;
}

/**
 * Shown when the answer is under 13. The learner stops here — they do not reach registration.
 *
 * ⚠️ THE COPY BELOW IS PROVISIONAL AND IS NOT APPROVED — KAN-493 (Paul).
 *
 * No under-13 string exists anywhere in the product: not in `learnCopy.js`, not in the i18n
 * bundle, not in any component. Rather than reuse a string written for a different situation
 * (the onboarding guardian note promises an approval step that does not exist), this states the
 * plainest true thing and nothing more. It makes no promise we cannot keep, gives no age back to
 * the learner, and does not suggest retrying with a different answer.
 *
 * It is deliberately dull, and it is Paul's to replace before this reaches real learners.
 */
export function FlareAgeIneligible({ onBack }) {
  return (
    <div data-testid="flare-age-ineligible">
      <h1 style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>
        Novara isn’t open to you just yet
      </h1>
      <p style={{ color: '#4b5563', marginBottom: '1.1rem' }}>
        You need to be 13 or older to create an account here. Nothing from today has been saved.
      </p>
      {onBack && (
        <div style={{ marginTop: '1.2rem' }}>
          <Button onClick={onBack}>Back to your results</Button>
        </div>
      )}
    </div>
  );
}

export default function FlareAgeGate({ onAnswer, busy = false }) {
  const [year, setYear] = useState('');
  const [touched, setTouched] = useState(false);

  const age = ageFromBirthYear(year);
  const valid = age !== null;

  const submit = () => {
    setTouched(true);
    if (!valid) return;
    onAnswer(parseInt(year, 10), isEligibleToStore(year));
  };

  const A = LEARN_COPY.onboarding.age;

  return (
    <div data-testid="flare-age-gate">
      <h1 style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>{A.q}</h1>
      <p style={{ color: '#4b5563', marginBottom: '1.1rem' }}>{A.blurb}</p>

      <input
        type="number"
        inputMode="numeric"
        aria-label={A.q}
        placeholder={A.placeholder}
        value={year}
        onChange={(e) => setYear(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        style={{ fontSize: '1rem', padding: '0.55rem 0.7rem', width: '10rem',
                 border: '1px solid #cbd5e1', borderRadius: 6 }}
      />

      {/* Shown only after an attempt: a validation message on an untouched field reads as
          an accusation before the learner has done anything. */}
      {touched && !valid && (
        <p role="alert" style={{ color: '#b91c1c', marginTop: '0.6rem' }}>
          {A.placeholder}
        </p>
      )}

      <div style={{ marginTop: '1.2rem' }}>
        <Button onClick={submit} disabled={busy}>Continue</Button>
      </div>
    </div>
  );
}
